import { trackCtaClick } from '#utils/utils';
import {
  Badge,
  Box,
  Button,
  Container,
  Grid,
  GridItem,
  HStack,
  Heading,
  Icon,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import React from 'react';
import { FaArrowRight, FaCheckCircle, FaClock, FaDollarSign, FaUsers } from 'react-icons/fa/index.js';
import {
  MdAutoAwesome,
  MdCalendarToday,
  MdDescription,
  MdEmail,
  MdPerson,
  MdPhone,
  MdReceipt,
  MdStorage,
} from 'react-icons/md/index.js';

const MotionBox = motion(Box);

// The manual process steps — shown as "before"
const MANUAL_STEPS = [
  { label: 'Customer calls or emails', icon: MdPhone },
  { label: 'Staff writes notes manually', icon: MdPerson },
  { label: 'Someone schedules the appointment', icon: MdCalendarToday },
  { label: 'Forms sent and collected by hand', icon: MdDescription },
  { label: 'Follow-up emails sent manually', icon: MdEmail },
  { label: 'Invoice created and sent manually', icon: MdReceipt },
  { label: 'CRM updated (if remembered)', icon: MdStorage },
  { label: 'Documents filed somewhere', icon: MdDescription },
];

// What gets automated
const AUTOMATED = [
  'New client inquiry captured automatically',
  'Intake form sent and collected without staff input',
  'Appointment scheduled based on real-time availability',
  'Confirmation and reminder emails sent on schedule',
  'CRM record created and updated automatically',
  'Invoice generated and sent upon completion',
  'Documents stored and linked to the client record',
  'Follow-up sequence triggered without anyone touching it',
];

const DELIVERABLES = [
  { step: '01', title: 'Discovery Session', desc: 'We map your current intake process end-to-end and identify every manual touchpoint.' },
  { step: '02', title: 'Workflow Map', desc: 'You receive a visual diagram of exactly what will be automated before we write a line of code.' },
  { step: '03', title: 'Implementation', desc: 'We build and configure every automation on the TenK Platform — integrated with your existing tools.' },
  { step: '04', title: 'Team Training', desc: 'Your staff learns how the system works and how to handle exceptions in under 2 hours.' },
  { step: '05', title: '30-Day Support', desc: 'We monitor, adjust, and support the system during your first full month of live operation.' },
];

const STATS = [
  { value: '8–12 hrs', label: 'Average hours saved per week', icon: FaClock },
  { value: '$18K+', label: 'Annual staff time value recovered', icon: FaDollarSign },
  { value: '< 4 wks', label: 'Typical implementation time', icon: FaUsers },
];

const ClientIntakePage = () => {
  const pageBg = useColorModeValue('gray.50', 'gray.950');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.900', 'white');
  const manualBg = useColorModeValue('red.50', 'red.900');
  const manualBorder = useColorModeValue('red.200', 'red.700');
  const autoBg = useColorModeValue('green.50', 'green.900');
  const autoBorder = useColorModeValue('green.200', 'green.700');

  return (
    <Box bg={pageBg}>
      {/* Hero */}
      <Box
        bgGradient="linear(to-br, blue.600, cyan.500)"
        py={{ base: 20, md: 32 }}
        position="relative"
        overflow="hidden"
      >
        {/* Decorative circles */}
        <Box
          position="absolute" top="-80px" right="-80px"
          w="400px" h="400px" borderRadius="full"
          bg="whiteAlpha.100" pointerEvents="none"
        />
        <Box
          position="absolute" bottom="-60px" left="-60px"
          w="300px" h="300px" borderRadius="full"
          bg="whiteAlpha.50" pointerEvents="none"
        />
        <Container maxW="container.xl">
          <Stack direction={{ base: 'column', lg: 'row' }} spacing={12} align="center">
            <MotionBox
              flex="1"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 } as any}
            >
              <Badge
                bg="whiteAlpha.200" color="white"
                fontSize="sm" px={4} py={1} borderRadius="full"
                mb={4} display="inline-block"
              >
                Solution 1 of 3
              </Badge>
              <Heading
                as="h1"
                fontSize={{ base: '3xl', md: '5xl' }}
                fontWeight="extrabold"
                color="white"
                lineHeight="1.1"
                letterSpacing="-0.03em"
                mb={6}
              >
                Client Intake
                <br />
                Automation
              </Heading>
              <Text fontSize="xl" color="whiteAlpha.800" mb={8} maxW="lg">
                From first contact to paid invoice — every step automated.
                Your staff stops being a manual relay system and starts focusing
                on the work that actually requires a human.
              </Text>
              <HStack spacing={4} flexWrap="wrap">
                <Button
                  size="lg"
                  bg="white"
                  color="blue.600"
                  fontWeight="bold"
                  px={8}
                  as="a"
                  href="/contact-us?interest=client-intake"
                  onClick={() => trackCtaClick({ cta_label: 'Map My Intake Process', cta_location: 'ClientIntake Hero', destination_url: '/contact-us?interest=client-intake' })}
                  _hover={{ bg: 'gray.50', transform: 'translateY(-2px)', boxShadow: 'xl' }}
                  transition="all 0.2s"
                >
                  Map My Intake Process
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  borderColor="whiteAlpha.600"
                  color="white"
                  fontWeight="bold"
                  px={8}
                  as="a"
                  href="/pricing"
                  _hover={{ bg: 'whiteAlpha.100', transform: 'translateY(-2px)' }}
                  transition="all 0.2s"
                >
                  See Pricing
                </Button>
              </HStack>
            </MotionBox>

            {/* Stats block */}
            <MotionBox
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 } as any}
              flex="0 0 auto"
            >
              <SimpleGrid columns={1} spacing={4} minW={{ md: '280px' }}>
                {STATS.map((s) => (
                  <Box
                    key={s.label}
                    bg="whiteAlpha.200"
                    backdropFilter="blur(10px)"
                    borderRadius="xl"
                    p={5}
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                  >
                    <HStack spacing={3}>
                      <Icon as={s.icon} boxSize={6} color="yellow.300" />
                      <Box>
                        <Text fontSize="2xl" fontWeight="extrabold" color="white" lineHeight="1">
                          {s.value}
                        </Text>
                        <Text fontSize="xs" color="whiteAlpha.700" mt={0.5}>
                          {s.label}
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                ))}
              </SimpleGrid>
            </MotionBox>
          </Stack>
        </Container>
      </Box>

      {/* Before / After */}
      <Box py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={14} textAlign="center">
            <Heading as="h2" fontSize={{ base: '2xl', md: '3xl' }} fontWeight="extrabold" color={headingColor} letterSpacing="-0.02em">
              The process before us. And after.
            </Heading>
            <Text fontSize="lg" color={textColor} maxW="2xl">
              Nothing changes about your business model. Everything changes about the work your team has to do manually.
            </Text>
          </VStack>

          <Grid templateColumns={{ base: '1fr', lg: '1fr auto 1fr' }} gap={8} alignItems="start">
            {/* Before */}
            <GridItem>
              <Box
                bg={manualBg}
                border="1px solid"
                borderColor={manualBorder}
                borderRadius="2xl"
                p={8}
              >
                <Badge colorScheme="red" mb={4} fontSize="sm" px={3} py={1} borderRadius="full">
                  Before TenK
                </Badge>
                <Heading as="h3" fontSize="lg" fontWeight="bold" color={headingColor} mb={6}>
                  8 manual steps. Every. Single. Client.
                </Heading>
                <VStack spacing={3} align="stretch">
                  {MANUAL_STEPS.map((s, i) => (
                    <MotionBox
                      key={s.label}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.07 } as any}
                    >
                      <HStack
                        spacing={3}
                        bg="white"
                        borderRadius="lg"
                        p={3}
                        boxShadow="sm"
                        opacity={0.85}
                      >
                        <Icon as={s.icon} color="red.400" boxSize={4} flexShrink={0} />
                        <Text fontSize="sm" color="gray.700">{s.label}</Text>
                        {i < MANUAL_STEPS.length - 1 && (
                          <Icon as={FaArrowRight} color="red.300" boxSize={3} ml="auto" />
                        )}
                      </HStack>
                    </MotionBox>
                  ))}
                </VStack>
              </Box>
            </GridItem>

            {/* Arrow */}
            <GridItem display={{ base: 'none', lg: 'flex' }} alignItems="center" justifyContent="center">
              <VStack spacing={2}>
                <Box w="2px" h="80px" bg="gray.300" />
                <Box
                  w={12} h={12} borderRadius="full"
                  bgGradient="linear(to-br, blue.500, cyan.400)"
                  display="flex" alignItems="center" justifyContent="center"
                  boxShadow="lg"
                >
                  <Icon as={MdAutoAwesome} color="white" boxSize={5} />
                </Box>
                <Box w="2px" h="80px" bg="gray.300" />
              </VStack>
            </GridItem>

            {/* After */}
            <GridItem>
              <Box
                bg={autoBg}
                border="1px solid"
                borderColor={autoBorder}
                borderRadius="2xl"
                p={8}
              >
                <Badge colorScheme="green" mb={4} fontSize="sm" px={3} py={1} borderRadius="full">
                  After TenK
                </Badge>
                <Heading as="h3" fontSize="lg" fontWeight="bold" color={headingColor} mb={6}>
                  Customer contacts you. Everything else happens.
                </Heading>
                <VStack spacing={3} align="stretch">
                  {AUTOMATED.map((item, i) => (
                    <MotionBox
                      key={item}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.07 } as any}
                    >
                      <HStack
                        spacing={3}
                        bg="white"
                        borderRadius="lg"
                        p={3}
                        boxShadow="sm"
                      >
                        <Icon as={FaCheckCircle} color="green.400" boxSize={4} flexShrink={0} />
                        <Text fontSize="sm" color="gray.700">{item}</Text>
                      </HStack>
                    </MotionBox>
                  ))}
                </VStack>
              </Box>
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {/* Deliverables */}
      <Box bg={useColorModeValue('gray.900', 'gray.950')} py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={14} textAlign="center">
            <Badge colorScheme="blue" fontSize="sm" px={4} py={1} borderRadius="full">The Process</Badge>
            <Heading as="h2" fontSize={{ base: '2xl', md: '3xl' }} fontWeight="extrabold" color="white" letterSpacing="-0.02em">
              What you get. Step by step.
            </Heading>
            <Text fontSize="lg" color="gray.400" maxW="xl">
              Every engagement follows the same five-step delivery process.
              No surprises. No scope creep.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={6}>
            {DELIVERABLES.map((d, i) => (
              <MotionBox
                key={d.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 } as any}
                bg="gray.800"
                border="1px solid"
                borderColor="gray.700"
                borderRadius="xl"
                p={6}
                _hover={{ borderColor: 'blue.400', transform: 'translateY(-4px)' }}
                style={{ transition: 'all 0.3s ease' }}
              >
                <Text
                  fontSize="3xl"
                  fontWeight="extrabold"
                  color="blue.400"
                  lineHeight="1"
                  mb={3}
                  fontFamily="mono"
                >
                  {d.step}
                </Text>
                <Heading as="h3" fontSize="md" fontWeight="bold" color="white" mb={2}>
                  {d.title}
                </Heading>
                <Text fontSize="sm" color="gray.400" lineHeight="1.6">
                  {d.desc}
                </Text>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Case Study Callout */}
      <Box py={{ base: 16, md: 20 }}>
        <Container maxW="container.md">
          <Box
            bg={cardBg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius="2xl"
            p={{ base: 8, md: 12 }}
            boxShadow="xl"
            textAlign="center"
          >
            <Badge colorScheme="green" mb={4} fontSize="sm" px={3} py={1} borderRadius="full">
              Real Result
            </Badge>
            <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="extrabold" color={headingColor} mb={4}>
              Smile Design Studio reduced IT infrastructure costs by 45% — and
              achieved zero downtime during their cloud migration.
            </Heading>
            <Text fontSize="md" color={textColor} mb={8} maxW="xl" mx="auto">
              We automated their intake, migrated their EMR to the cloud, and
              integrated 3 new operatory stations post-acquisition. Their team
              stopped worrying about IT and started focusing on patients.
            </Text>
            <Button
              as="a"
              href="/case-studies/smile-design-studio"
              colorScheme="blue"
              size="lg"
              px={8}
              rightIcon={<Icon as={FaArrowRight} />}
              _hover={{ transform: 'translateX(2px)' }}
              transition="all 0.2s"
            >
              Read the Full Case Study
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box
        bgGradient="linear(to-r, blue.600, cyan.500)"
        py={{ base: 16, md: 20 }}
      >
        <Container maxW="container.md" textAlign="center">
          <Heading
            as="h2"
            fontSize={{ base: '2xl', md: '4xl' }}
            fontWeight="extrabold"
            color="white"
            mb={4}
            letterSpacing="-0.02em"
          >
            Ready to eliminate your intake process?
          </Heading>
          <Text fontSize="lg" color="whiteAlpha.800" mb={8}>
            Book a free 30-minute discovery call. We'll map your current process
            and show you exactly what can be automated.
          </Text>
          <HStack spacing={4} justify="center" flexWrap="wrap">
            <Button
              size="lg"
              bg="white"
              color="blue.600"
              fontWeight="bold"
              px={10}
              as="a"
              href="/contact-us?interest=client-intake&package=launch"
              onClick={() => trackCtaClick({ cta_label: 'Book Discovery Call - Intake', cta_location: 'ClientIntake CTA', destination_url: '/contact-us?interest=client-intake' })}
              _hover={{ bg: 'gray.50', transform: 'translateY(-2px)', boxShadow: 'xl' }}
              transition="all 0.2s"
            >
              Book a Free Discovery Call
            </Button>
            <Button
              size="lg"
              variant="outline"
              borderColor="whiteAlpha.600"
              color="white"
              px={8}
              as="a"
              href="/pricing"
              _hover={{ bg: 'whiteAlpha.100' }}
            >
              Starting at $3,000
            </Button>
          </HStack>
        </Container>
      </Box>
    </Box>
  );
};

export default ClientIntakePage;
